import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

// Feature flag: when REACT_APP_REQUIRE_SUPPORTER is not set to "true", supporter
// gates are disabled and every user is treated as a supporter regardless of
// Clerk metadata. Set REACT_APP_REQUIRE_SUPPORTER=true to restore the paywall.
const SUPPORTER_GATE_ENABLED = process.env.REACT_APP_REQUIRE_SUPPORTER === 'true';

export const useSupporter = () => {
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [supporterStatus, setSupporterStatus] = useState({
    isSupporter: !SUPPORTER_GATE_ENABLED,
    supporterLevel: null,
    supporterSince: null,
    formerSupporter: false,
    loading: SUPPORTER_GATE_ENABLED,
    error: null
  });

  useEffect(() => {
    if (!SUPPORTER_GATE_ENABLED) {
      setSupporterStatus({
        isSupporter: true,
        supporterLevel: null,
        supporterSince: null,
        formerSupporter: false,
        loading: false,
        error: null,
        authenticated: isAuthenticated
      });
      return;
    }

    const fetchSupporterStatus = async () => {
      try {
        setSupporterStatus(prev => ({ ...prev, loading: true, error: null }));

        const headers = {
          'Content-Type': 'application/json'
        };

        // Add auth header if user is authenticated
        if (isAuthenticated && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch('/api/me/supporter', {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch supporter status: ${response.statusText}`);
        }

        const data = await response.json();

        setSupporterStatus({
          isSupporter: data.isSupporter || data.supporter || false,
          supporterLevel: data.supporterLevel || null,
          supporterSince: data.supporterSince || null,
          formerSupporter: data.formerSupporter || false,
          metadata: data.metadata || {},
          loading: false,
          error: null,
          authenticated: data.authenticated !== false
        });

      } catch (error) {
        console.error('Error fetching supporter status:', error);
        setSupporterStatus(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    };

    // Only fetch if auth is loaded and user is authenticated
    if (!authLoading) {
      if (isAuthenticated) {
        fetchSupporterStatus();
      } else {
        // User is not authenticated, set default non-supporter state
        setSupporterStatus({
          isSupporter: false,
          supporterLevel: null,
          supporterSince: null,
          formerSupporter: false,
          loading: false,
          error: null,
          authenticated: false
        });
      }
    }
  }, [isAuthenticated, token, authLoading]);

  const refreshSupporterStatus = async () => {
    if (!SUPPORTER_GATE_ENABLED) {
      setSupporterStatus({
        isSupporter: true,
        supporterLevel: null,
        supporterSince: null,
        formerSupporter: false,
        loading: false,
        error: null,
        authenticated: isAuthenticated
      });
      return;
    }
    if (!authLoading) {
      if (!isAuthenticated) {
        // User is not authenticated, set default non-supporter state
        setSupporterStatus({
          isSupporter: false,
          supporterLevel: null,
          supporterSince: null,
          formerSupporter: false,
          loading: false,
          error: null,
          authenticated: false
        });
        return;
      }

      const fetchSupporterStatus = async () => {
        // Same logic as above - could be extracted to avoid duplication
        try {
          setSupporterStatus(prev => ({ ...prev, loading: true, error: null }));

          const headers = {
            'Content-Type': 'application/json'
          };

          if (isAuthenticated && token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await fetch('/api/me/supporter', {
            method: 'GET',
            headers
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch supporter status: ${response.statusText}`);
          }

          const data = await response.json();

          setSupporterStatus({
            isSupporter: data.isSupporter || false,
            supporterLevel: data.supporterLevel || null,
            supporterSince: data.supporterSince || null,
            formerSupporter: data.formerSupporter || false,
            metadata: data.metadata || {},
            loading: false,
            error: null,
            authenticated: data.authenticated !== false
          });

        } catch (error) {
          console.error('Error refreshing supporter status:', error);
          setSupporterStatus(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        }
      };

      await fetchSupporterStatus();
    }
  };

  return {
    ...supporterStatus,
    refreshSupporterStatus,
    
    // Helper methods
    isSupporterLevel: (level) => {
      return supporterStatus.isSupporter && supporterStatus.supporterLevel === level;
    },
    
    isPremiumSupporter: () => {
      return supporterStatus.isSupporter && 
             ['premium', 'pro', 'enterprise'].includes(supporterStatus.supporterLevel);
    },
    
    hasBeenSupporter: () => {
      return supporterStatus.isSupporter || supporterStatus.formerSupporter;
    },
    
    getSupporterSinceDate: () => {
      return supporterStatus.supporterSince ? new Date(supporterStatus.supporterSince) : null;
    },
    
    getSupporterDuration: () => {
      if (!supporterStatus.supporterSince) return null;
      
      const since = new Date(supporterStatus.supporterSince);
      const now = new Date();
      const diffTime = Math.abs(now - since);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 30) {
        return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months === 1 ? '' : 's'}`;
      } else {
        const years = Math.floor(diffDays / 365);
        return `${years} year${years === 1 ? '' : 's'}`;
      }
    }
  };
};

export default useSupporter;